import { z } from "zod";
import { baseEntity, isoDate, isoDateTime, cents, nonNegCents, phone, email, id } from "./common.js";
import {
  Role, LeadStatus, LeadSource, ClientStatus, DocType, RentalStatus,
  PaymentDueStatus, PaymentMethod, VehicleStatus, FuelType, ShopVisitStatus, DepositStatus,
} from "./enums.js";

/** ---------- Notification preferences ---------- */
export const NOTIFY_EVENTS = [
  "maintenanceDue", "paymentReceived", "paymentOverdue", "rentalReadyForPickup",
  "rentalEndingSoon", "newLead", "tabExpiring", "licenseExpiring",
] as const;
const NotifChannel = z.object({ email: z.boolean().default(false), sms: z.boolean().default(false) }).default({});
export const NotificationPrefs = z.object({
  email: email.optional(),
  phone: phone.optional(),
  events: z.object({
    maintenanceDue: NotifChannel,
    paymentReceived: NotifChannel,
    paymentOverdue: NotifChannel,
    rentalReadyForPickup: NotifChannel,
    rentalEndingSoon: NotifChannel,
    newLead: NotifChannel,
    tabExpiring: NotifChannel,
    licenseExpiring: NotifChannel,
  }).default({}),
});
export type NotificationPrefs = z.infer<typeof NotificationPrefs>;

/** ---------- Tenant / Settings ---------- */
export const TenantSettings = baseEntity.extend({
  type: z.literal("Tenant"),
  name: z.string().min(1),
  timezone: z.string().min(1),
  paymentGraceDays: z.number().int().min(0),
  prorationDaysPerYear: z.union([z.literal(365), z.literal(360)]),
  lateFee: z.object({ kind: z.enum(["FLAT", "PERCENT"]), value: z.number().min(0) }).optional(),
  currency: z.string().default("USD"),
  notifications: NotificationPrefs.optional(),
});

/** ---------- User ---------- */
export const User = baseEntity.extend({
  type: z.literal("User"),
  email,
  role: Role,
  name: z.string().min(1),
});

/** ---------- Lead ---------- */
export const LeadActivity = z.object({
  at: isoDateTime, note: z.string().min(1), byUserId: id.optional(),
});
export const Lead = baseEntity.extend({
  type: z.literal("Lead"),
  name: z.string().min(1),
  phone, email: email.optional(),
  vehiclesOfInterest: z.array(id).default([]),
  desiredStartDate: isoDate.optional(),
  desiredDurationMonths: z.number().int().min(1).optional(),
  source: LeadSource,
  status: LeadStatus,
  lostReason: z.string().optional(),
  activity: z.array(LeadActivity).default([]),
  convertedClientId: id.optional(),
});

/** ---------- Client ---------- */
export const Client = baseEntity.extend({
  type: z.literal("Client"),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone, email: email.optional(),
  status: ClientStatus,
  address: z.string().optional(),
  dob: isoDate.optional(),
  emergencyContact: z.object({ name: z.string(), phone }).optional(),
  insurance: z.object({
    provider: z.string(), policyNumber: z.string(), expirationDate: isoDate.optional(),
  }).optional(),
  /** red-banner override acknowledgement when docs are expired (logged) */
  complianceOverrideAt: isoDateTime.optional(),
  notes: z.array(LeadActivity).default([]),
});

/** ---------- Document ---------- */
export const Document = baseEntity.extend({
  type: z.literal("Document"),
  clientId: id,
  docType: DocType,
  s3Key: z.string().min(1),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().min(0),
  uploadConfirmed: z.boolean().default(false),
  // license-specific
  licenseNumber: z.string().optional(),
  licenseState: z.string().optional(),
  licenseClass: z.string().optional(),
  expirationDate: isoDate.optional(),
});

/** ---------- Vehicle ---------- */
export const Vehicle = baseEntity.extend({
  type: z.literal("Vehicle"),
  make: z.string().min(1), model: z.string().min(1),
  year: z.number().int().min(1900).max(2100),
  trim: z.string().optional(), color: z.string().optional(),
  vin: z.string().length(17),
  plate: z.string().optional(), plateState: z.string().optional(),
  tabExpirationDate: isoDate.optional(),
  photos: z.array(z.string()).default([]),
  purchaseDate: isoDate.optional(),
  purchasePriceCents: nonNegCents.optional(),
  odometer: z.number().int().min(0),
  fuelType: FuelType,
  monthlyRateCents: nonNegCents,
  status: VehicleStatus,
  notes: z.string().optional(),
});

export const OdometerReading = baseEntity.extend({
  type: z.literal("OdometerReading"),
  vehicleId: id, miles: z.number().int().min(0),
  readAt: isoDateTime, source: z.enum(["CHECKOUT", "RETURN", "SERVICE", "MANUAL"]),
  overrideReason: z.string().optional(),
});

/** ---------- Rental ---------- */
export const ConditionReport = z.object({
  kind: z.enum(["CHECKOUT", "RETURN"]),
  at: isoDateTime,
  odometer: z.number().int().min(0),
  fuelOrCharge: z.string().optional(),
  checklist: z.record(z.string(), z.boolean()).default({}),
  photos: z.array(z.string()).default([]),
  notes: z.string().optional(),
});
export const RentalSwap = z.object({
  swapId: id, at: isoDateTime,
  fromVehicleId: id, toVehicleId: id,
  fromVehicleOdoClose: z.number().int().min(0),
  toVehicleOdoOpen: z.number().int().min(0),
  reason: z.string().optional(),
});
export const Rental = baseEntity.extend({
  type: z.literal("Rental"),
  clientId: id, vehicleId: id,
  startDate: isoDate,
  minimumTermMonths: z.number().int().min(1),
  endDate: isoDate.nullable(),               // null = open-ended month-to-month
  monthlyRateCents: nonNegCents,
  securityDeposit: z.object({
    amountCents: nonNegCents, status: DepositStatus,
    returnedCents: nonNegCents.default(0), reason: z.string().optional(),
  }),
  billingDayOfMonth: z.number().int().min(1).max(31),
  mileageAtCheckout: z.number().int().min(0),
  monthlyMileageCap: z.number().int().min(0).optional(),
  overageRateCentsPerMile: nonNegCents.optional(),
  additionalDrivers: z.array(z.object({ name: z.string(), licenseDocId: id.optional() })).default([]),
  status: RentalStatus,
  terminationReason: z.string().optional(),
  earlyTerminationFeeCents: nonNegCents.optional(),
  swaps: z.array(RentalSwap).default([]),
  conditionReports: z.array(ConditionReport).default([]),
});

/** ---------- Payments ---------- */
export const PaymentDue = baseEntity.extend({
  type: z.literal("PaymentDue"),
  rentalId: id, clientId: id,
  periodStart: isoDate, periodEnd: isoDate,
  dueDate: isoDate,
  amountCents: nonNegCents,             // expected (after proration)
  status: PaymentDueStatus,
  lateFeeCents: nonNegCents.default(0),
});
export const Payment = baseEntity.extend({
  type: z.literal("Payment"),
  dueId: id, rentalId: id, clientId: id,
  amountCents: cents,                   // may be negative for a correction credit
  receivedAt: isoDate,
  method: PaymentMethod,
  externalRef: z.string().optional(),   // future processor hook
  reference: z.string().optional(),
  notes: z.string().optional(),
  voidedAt: isoDateTime.optional(),
  voidReason: z.string().optional(),
});

/** ---------- Maintenance ---------- */
export const MaintenanceSchedule = baseEntity.extend({
  type: z.literal("MaintenanceSchedule"),
  vehicleId: id, serviceType: z.string().min(1),
  everyMiles: z.number().int().min(0).optional(),
  everyMonths: z.number().int().min(0).optional(),
  lastServiceDate: isoDate.optional(),
  lastServiceOdometer: z.number().int().min(0).optional(),
  nextDueDate: isoDate.optional(),
  nextDueOdometer: z.number().int().min(0).optional(),
}).refine((s) => s.everyMiles || s.everyMonths, { message: "schedule needs everyMiles or everyMonths" });

export const MaintenanceLog = baseEntity.extend({
  type: z.literal("MaintenanceLog"),
  vehicleId: id, performedAt: isoDate, odometer: z.number().int().min(0),
  serviceTypes: z.array(z.string().min(1)).min(1),
  description: z.string().optional(),
  partsCents: nonNegCents.optional(), laborCents: nonNegCents.optional(),
  totalCents: nonNegCents,
  performedBy: z.string().optional(),   // mechanicId or "self"
  receiptS3Key: z.string().optional(),
  shopVisitId: id.optional(),
});

/** ---------- Shop ---------- */
export const Mechanic = baseEntity.extend({
  type: z.literal("Mechanic"),
  name: z.string().min(1), businessName: z.string().optional(),
  phone: phone.optional(), address: z.string().optional(),
  specialties: z.array(z.string()).default([]),
  rating: z.number().int().min(1).max(5).optional(),
  notes: z.string().optional(),
});
export const ShopVisit = baseEntity.extend({
  type: z.literal("ShopVisit"),
  vehicleId: id, mechanicId: id.optional(),
  dropOffDate: isoDate, expectedReturnDate: isoDate.optional(), actualReturnDate: isoDate.optional(),
  workRequested: z.string().min(1),
  status: ShopVisitStatus,
  estimateCents: nonNegCents.optional(), finalCents: nonNegCents.optional(),
  photos: z.array(z.string()).default([]),
  notes: z.array(z.object({ at: isoDateTime, note: z.string() })).default([]),
});

/** ---------- Audit & Notifications ---------- */
export const AuditEvent = baseEntity.extend({
  type: z.literal("AuditEvent"),
  action: z.enum(["CREATE", "UPDATE", "DELETE", "OVERRIDE"]),
  entityType: z.string(), entityId: id,
  byUserId: id,
  diff: z.record(z.string(), z.object({ from: z.unknown(), to: z.unknown() })).optional(),
  reason: z.string().optional(),
});
export const Notification = baseEntity.extend({
  type: z.literal("Notification"),
  category: z.string(), message: z.string(), deepLink: z.string().optional(),
  read: z.boolean().default(false),
});

/** Inferred types */
export type TenantSettings = z.infer<typeof TenantSettings>;
export type User = z.infer<typeof User>;
export type Lead = z.infer<typeof Lead>;
export type Client = z.infer<typeof Client>;
export type Document = z.infer<typeof Document>;
export type Vehicle = z.infer<typeof Vehicle>;
export type OdometerReading = z.infer<typeof OdometerReading>;
export type Rental = z.infer<typeof Rental>;
export type RentalSwap = z.infer<typeof RentalSwap>;
export type ConditionReport = z.infer<typeof ConditionReport>;
export type PaymentDue = z.infer<typeof PaymentDue>;
export type Payment = z.infer<typeof Payment>;
export type MaintenanceSchedule = z.infer<typeof MaintenanceSchedule>;
export type MaintenanceLog = z.infer<typeof MaintenanceLog>;
export type Mechanic = z.infer<typeof Mechanic>;
export type ShopVisit = z.infer<typeof ShopVisit>;
export type AuditEvent = z.infer<typeof AuditEvent>;
export type Notification = z.infer<typeof Notification>;
